import { useMemo } from 'react'
import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useLazyGetProjectsPageQuery } from '../../../../api'
import type { TGetProjectsQueryParams } from '../../../../api/types'
import { ProjectsList } from '../../../../components/ProjectsPage/ProjectsList'
import { useProjectsWindowedPage } from '../../../../hooks/useProjectsWindowedPage'
import { normalizeProjectTagForFilter } from '../../../../utils/projectTags'

export const ProjectsAll = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const projectsQueryArgs = useMemo<TGetProjectsQueryParams>(() => {
    const args: TGetProjectsQueryParams = {}
    const query = searchParams.get('query')?.trim() ?? ''
    const type = searchParams.get('type')?.trim() ?? ''
    const stage = searchParams.get('stage')?.trim() ?? ''
    const tag = searchParams.get('tag')?.trim() ?? ''

    if (query) {
      args.query = query
    }
    if (type && type !== 'all') {
      args.type = type
    }
    if (stage && stage !== 'all') {
      args.stage = stage
    }
    if (tag) {
      args.tag = tag
    }

    return args
  }, [searchParams])

  const activeTagLabel = useMemo(
    () => normalizeProjectTagForFilter(searchParams.get('tag') ?? ''),
    [searchParams],
  )

  const resetKey = useMemo(() => JSON.stringify(projectsQueryArgs), [projectsQueryArgs])
  const [loadProjectsPage] = useLazyGetProjectsPageQuery()
  const {
    items,
    hasMore,
    isInitialLoading,
    isFetchingMore,
    isError,
    loadMore,
  } = useProjectsWindowedPage(resetKey, loadProjectsPage, projectsQueryArgs)

  const clearTagFilter = () => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('tag')
    setSearchParams(nextParams, { replace: true })
  }

  if (isInitialLoading && items.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (isError) {
    return (
      <Typography color="error" textAlign="center" px={2}>
        Не удалось загрузить проекты
      </Typography>
    )
  }

  return (
    <Stack gap={1.5} height="100%">
      {activeTagLabel && (
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} px={{ xs: 0, md: 0 }}>
          <Typography variant="black-14-light">
            Направление: <Typography component="span" variant="black-14-medium">{activeTagLabel}</Typography>
          </Typography>
          <Button type="button" size="small" variant="text" onClick={clearTagFilter}>
            Сбросить
          </Button>
        </Stack>
      )}
      <ProjectsList
        projects={items}
        hasMore={hasMore}
        isLoadingMore={isFetchingMore && hasMore}
        onLoadMore={() => void loadMore()}
      />
    </Stack>
  )
}
